<template>
	<div class="g-header-ip-brand-preview" :class="{
		'g-header-ip-brand-preview--h5': h5,
		'g-header-ip-brand-preview--compact': compact,
	}" @click="onClick">
		<img v-if="bgImageUrl" :src="bgImageUrl" class="g-header-ip-brand-preview__bg" alt="" />
		<div class="g-header-ip-brand-preview__content-wrapper">
			<div class="g-header-ip-brand-preview__content">
				<div v-if="normalizedData.showBadge && normalizedData.badge" class="g-header-ip-brand-preview__badge"
					:style="getBadgeStyle(normalizedData)">{{ normalizedData.badge }}</div>
				<div v-if="normalizedData.title" class="g-header-ip-brand-preview__title"
					:style="{ color: normalizedData.textColor }">{{ normalizedData.title }}</div>
				<div v-if="normalizedData.subtitle" class="g-header-ip-brand-preview__subtitle"
					:style="{ color: normalizedData.textColor }">{{ normalizedData.subtitle }}</div>
				<div v-if="normalizedData.showFeatures && normalizedData.features.length"
					class="g-header-ip-brand-preview__features">
					<div v-for="(item, index) in normalizedData.features" :key="item.id"
						class="g-header-ip-brand-preview__feature" :style="{ color: normalizedData.textColor }">
						<span v-if="index > 0" class="g-header-ip-brand-preview__divider" aria-hidden="true"></span>
						<span class="g-header-ip-brand-preview__feature-icon" :style="{ background: normalizedData.featureIconColor }">
							<i class="mbm-iconfont" :class="item.icon"></i>
						</span>
						<span class="g-header-ip-brand-preview__feature-label">{{ item.label }}</span>
					</div>
				</div>
			</div>
		</div>

		<div v-if="normalizedData.showTag && normalizedData.tag" class="g-header-ip-brand-preview__tag"
			:style="getTagStyle(normalizedData)">{{ normalizedData.tag }}</div>
	</div>
</template>

<script>
import { navigateJumpLink } from '../utils/jumpLink.js';
import {
	getHeaderIpBrandImageUrl,
	normalizeHeaderIpBrandData,
} from '../utils/headerIpBrand.js';

export default {
	name: 'GHeaderIpBrandPreview',
	props: {
		data: {
			type: Object,
			default: () => ({}),
		},
		h5: {
			type: Boolean,
			default: false,
		},
		compact: {
			type: Boolean,
			default: false,
		},
		interactive: {
			type: Boolean,
			default: true,
		},
	},
	computed: {
		normalizedData() {
			return normalizeHeaderIpBrandData(this.data);
		},
		rootStyle() {
			const height = this.normalizedData.height;
			const radius = this.normalizedData.borderRadius;
			const unit = this.h5 ? 'rem' : 'px';
			const heightValue = this.h5 ? height / 100 : height;
			const radiusValue = this.h5 ? radius / 50 : radius;
			const radiusCss = `${radiusValue}${unit}`;
			return {
				'--g-header-ip-brand-height': `${heightValue}${unit}`,
				'--g-header-ip-brand-radius': radiusCss,
				borderRadius: radiusCss,
			};
		},
		bgImageUrl() {
			return getHeaderIpBrandImageUrl(this.normalizedData.image);
		},
	},
	methods: {
		onClick() {
			if (!this.h5 || !this.interactive || !this.normalizedData.jumpLink?.type) return;
			navigateJumpLink(this.normalizedData.jumpLink, {
				apiRequest: this.$apiRequest,
			});
		},
		getBadgeStyle(data) {
			const bgColor = data.badgeTagBgColor || '#325fb2';
			const textColor = data.textColor || '#ffffff';
			return {
				color: textColor,
				background: `linear-gradient(90deg, ${bgColor} 0%, ${bgColor}aa 100%)`,
			};
		},
		getTagStyle(data) {
			const bgColor = data.badgeTagBgColor || '#325fb2';
			const textColor = data.textColor || '#ffffff';
			return {
				color: textColor,
				background: `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}aa 100%)`,
			};
		},
	},
};
</script>

<style scoped lang="less">
.g-header-ip-brand-preview {
	position: relative;
	width: 100%;
	height: var(--g-header-ip-brand-height, 300px);
	overflow: hidden;
	background: #163a8a;
}

.g-header-ip-brand-preview__bg {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: right center;
}

.g-header-ip-brand-preview__content {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	max-width: calc(100% - 56px);
	box-sizing: border-box;
}
.g-header-ip-brand-preview__content-wrapper{
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
	z-index: 2;
	display: flex;
	align-items: center;
	padding-left: 28px;
}
.g-header-ip-brand-preview__badge {
	display: inline-flex;
	align-items: center;
	max-width: 100%;
	padding: 5px 11px;
	border-radius: 999px;
	font-size: 11px;
	font-weight: 500;
	line-height: 1.3;
	letter-spacing: 0.02em;
	white-space: nowrap;
}

.g-header-ip-brand-preview__title {
	max-width: 240px;
	margin-top: 10px;
	font-size: 27px;
	font-weight: 700;
	line-height: 1.35;
	white-space: pre-line;
	word-break: break-word;
	text-shadow: 0 2px 10px rgba(8, 28, 80, 0.25);
}

.g-header-ip-brand-preview__subtitle {
	max-width: 240px;
	margin-top: 10px;
	font-size: 11px;
	font-weight: 400;
	line-height: 1.5;
	word-break: break-word;
}

.g-header-ip-brand-preview__features {
	display: flex;
	align-items: stretch;
	justify-content: flex-start;
	width: auto;
	max-width: 100%;
	margin-top: 13px;
	padding-top: 0;
	gap: 0;
	margin-left: -10px;
}

.g-header-ip-brand-preview__feature {
	position: relative;
	display: flex;
	flex: 0 0 auto;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 60px;
	padding: 0;
	text-align: center;
}

.g-header-ip-brand-preview__feature-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 26px;
	height: 26px;
	border-radius: 50%;
	background: #3039b4;

	i {
		font-size: 14px;
		line-height: 1;
		opacity: 0.95;
	}
}

.g-header-ip-brand-preview__feature-label {
	font-size: 10px;
	font-weight: 500;
	line-height: 1.3;
	white-space: nowrap;
}

.g-header-ip-brand-preview__divider {
	position: absolute;
	left: 0;
	top: 50%;
	width: 1px;
	height: 28px;
	transform: translateY(-50%);
	background: rgba(255, 255, 255, 0.28);
	pointer-events: none;
}

.g-header-ip-brand-preview__tag {
	position: absolute;
	right: 10%;
	top: 66%;
	z-index: 3;
	padding: 3px 12px;
	border-radius: 999px;
	font-size: 12px;
	font-weight: 600;
	line-height: 1.3;
	white-space: nowrap;
	box-shadow: 0 6px 16px rgba(40, 60, 180, 0.28);
	pointer-events: none;
}

.g-header-ip-brand-preview--h5 {
	height: 4.1rem;

	.g-header-ip-brand-preview__content {
		left: 0.6rem;
		bottom: 0.3rem;
		max-width: calc(100% - 0.72rem);
	}

	.g-header-ip-brand-preview__content-wrapper{
		padding-left: 0.48rem;
	}

	.g-header-ip-brand-preview__badge {
		padding: 0.1rem 0.2rem;
		font-size: 0.2rem;
	}

	.g-header-ip-brand-preview__title {
		max-width: 4rem;
		margin-top: 0.16rem;
		font-size: 0.46rem;
	}

	.g-header-ip-brand-preview__subtitle {
		max-width: 4rem;
		margin-top: 0.14rem;
		font-size: 0.2rem;
	}

	.g-header-ip-brand-preview__features {
		margin-top: 0.2rem;
		padding-top: 0;
		margin-left: -0.14rem;
	}

	.g-header-ip-brand-preview__feature {
		width: 1.1rem;
		gap: 0.12rem;
	}

	.g-header-ip-brand-preview__feature-icon {
		width: 0.42rem;
		height: 0.42rem;

		i {
			font-size: 0.22rem;
		}
	}

	.g-header-ip-brand-preview__feature-label {
		font-size: 0.2rem;
	}

	.g-header-ip-brand-preview__divider {
		height: 0.48rem;
	}

	.g-header-ip-brand-preview__tag {
		right: 0.28rem;
		top: 66%;
		padding: 0.06rem 0.18rem;
		font-size: 0.18rem;
	}
}

.g-header-ip-brand-preview--compact {
	height: 192px;

	.g-header-ip-brand-preview__content {
		left: 30px;
		bottom: 15px;
		max-width: calc(100% - 24px);
	}

	.g-header-ip-brand-preview__content-wrapper{
		padding-left: 24px;
	}

	.g-header-ip-brand-preview__badge {
		padding: 5px 10px;
		font-size: 10px;
	}

	.g-header-ip-brand-preview__title {
		max-width: 140px;
		margin-top: 8px;
		font-size: 23px;
	}

	.g-header-ip-brand-preview__subtitle {
		max-width: 240px;
		margin-top: 7px;
		font-size: 10px;
	}

	.g-header-ip-brand-preview__features {
		margin-top: 10px;
		padding-top: 0;
		margin-left: -6px;
	}

	.g-header-ip-brand-preview__feature {
		width: 55px;
		gap: 4px;
	}

	.g-header-ip-brand-preview__feature-icon {
		width: 21px;
		height: 21px;

		i {
			font-size: 11px;
		}
	}

	.g-header-ip-brand-preview__feature-label {
		font-size: 10px;
	}

	.g-header-ip-brand-preview__divider {
		height: 16px;
	}

	.g-header-ip-brand-preview__tag {
		right: 8%;
		padding: 2px 7px;
		font-size: 9px;
	}
}
</style>
